import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiCheckCircle,FiUsers,FiBriefcase}=FiIcons;

const ProfessionalLiability=()=> {
  const coverageItems=[ 
    'Negligence claims','Malpractice allegations','Errors and omissions','Misrepresentation claims','Incomplete or inaccurate work','Breaches of contract','Legal defense costs','Settlement and judgment payments' 
  ];

  const protectionAreas=[ 
    {title: 'Failure to Adhere to Contract',description: 'Protection when you fail to meet contractual obligations or agreements with clients.'},{title: 'Delayed Delivery',description: 'Coverage when you fail to deliver a final product or service in a timely fashion,causing client harm.'},{title: 'Inaccurate Advice or Service',description: 'Protection when you fail to provide accurate or correct advice,product,or service to clients.'} 
  ];

  const businessTypes=[ 
    'Produce specific work on behalf of someone else','Generate reports,make recommendations,or provide opinions','Require some degree of skill,a license,or training to operate' 
  ];

  const professions=[ 
    'Attorneys','Accountants','Architects','Engineers','Designers','Photographers','Financial Advisors','Real Estate Agents','Consultants','IT Professionals','Healthcare Providers','Insurance Agents' 
  ];

  const californiaKeywords=[ 
    'Los Angeles','San Francisco','San Diego','Sacramento','San Jose','Fresno','Long Beach','Oakland','Bakersfield','Anaheim','Santa Ana','Riverside','Stockton','Irvine','Chula Vista' 
  ];

  return ( 
    <div className="min-h-screen"> 
      {/* Hero Section */} 
      <section className="relative bg-gray-900 text-white overflow-hidden"> 
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" 
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',}} 
        /> 
        <div className="absolute inset-0 bg-purple-900 bg-opacity-80" /> 
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24"> 
          <div className="text-center"> 
            <motion.h1 
              initial={{opacity: 0,y: 30}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 0.8}} 
              className="text-4xl md:text-5xl font-bold mb-6" 
            > 
              Professional Liability Insurance for Small Business 
            </motion.h1> 
            <motion.p 
              initial={{opacity: 0,y: 30}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 0.8,delay: 0.2}} 
              className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto" 
            > 
              Professional Liability (MPL) insurance (also known as Errors & Omissions or E&O insurance) helps protect your small business from failure to uphold contractual promises in mostly service-based professions. 
            </motion.p> 
            <motion.div 
              initial={{opacity: 0,y: 30}} 
              animate={{opacity: 1,y: 0}} 
              transition={{duration: 0.8,delay: 0.4}} 
              className="flex justify-center" 
            > 
              <a href="https://coterie.com/quote" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl" 
              > 
                Get My Price 
              </a> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* What It Covers Section */} 
      <section className="py-20 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-16"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> What Professional Liability Insurance Covers </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> Professional Liability insurance pays for financial or economic damages to your customers or clients as a result of professional mistakes and oversights. </p> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
            {coverageItems.map((item,index)=> ( 
              <motion.div 
                key={index} 
                initial={{opacity: 0,x: -30}} 
                animate={{opacity: 1,x: 0}} 
                transition={{duration: 0.6,delay: index * 0.1}} 
                className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4" 
              > 
                <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-purple-500 flex-shrink-0" /> 
                <span className="text-gray-800 font-medium">{item}</span> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Real-World Example */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-8 rounded-xl"> 
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Real-World Example</h3> 
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"> 
              <div> 
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Missed Deadline Scenario</h4> 
                <p className="text-gray-700 mb-4"> Consider a situation in which,during the normal course of running your small business,you make a mistake that impacts a client. You'd be held responsible for legal fees and damages unless you're protected by errors and omissions insurance. </p> 
                <p className="text-gray-700"> Similarly,if you're working with a client who depends on you to fulfill a service by a concrete deadline and you miss the deadline,causing a negative impact to your client,your client can file suit against you. </p> 
              </div> 
              <div className="bg-white p-6 rounded-lg shadow-lg"> 
                <h5 className="font-semibold text-gray-900 mb-3">Professional Liability Would Cover:</h5> 
                <ul className="space-y-2"> 
                  <li className="flex items-center space-x-2"> 
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" /> 
                    <span className="text-gray-700">Legal defense costs</span> 
                  </li> 
                  <li className="flex items-center space-x-2"> 
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" /> 
                    <span className="text-gray-700">Damages awarded to client</span> 
                  </li> 
                  <li className="flex items-center space-x-2"> 
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" /> 
                    <span className="text-gray-700">Settlement costs</span> 
                  </li> 
                  <li className="flex items-center space-x-2"> 
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500" /> 
                    <span className="text-gray-700">Business protection from closure</span> 
                  </li> 
                </ul> 
              </div> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* Protection Areas */} 
      <section className="py-20 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-16"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> Areas of Professional Protection </h2> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> Professional Liability insurance protects you from wrongful acts and economic damages not covered by GL insurance or a BOP. </p> 
          </div> 
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8"> 
            {protectionAreas.map((area,index)=> ( 
              <motion.div 
                key={index} 
                initial={{opacity: 0,y: 30}} 
                animate={{opacity: 1,y: 0}} 
                transition={{duration: 0.6,delay: index * 0.1}} 
                className="bg-white p-8 rounded-xl shadow-lg text-center" 
              > 
                <SafeIcon icon={FiShield} className="h-12 w-12 text-purple-600 mx-auto mb-4" /> 
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{area.title}</h3> 
                <p className="text-gray-600">{area.description}</p> 
              </motion.div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Who Needs It Section */} 
      <section className="py-20 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"> 
            <motion.div 
              initial={{opacity: 0,x: -30}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8}} 
            > 
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> Who Needs Professional Liability Insurance? </h2> 
              <p className="text-lg text-gray-600 mb-6"> This type of insurance is critical for any small business that provides services or works on a contractual basis. Where General Liability and Business Owners Policies protect against physical risks,Professional Liability insurance covers liabilities arising from errors,omissions,and other wrongful acts as a result of your profession. </p> 
              <h3 className="text-xl font-semibold text-gray-900 mb-4"> Professional Liability is especially necessary for small businesses that: </h3> 
              <div className="space-y-3"> 
                {businessTypes.map((type,index)=> ( 
                  <div key={index} className="flex items-start space-x-3"> 
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" /> 
                    <span className="text-gray-700">{type}</span> 
                  </div> 
                ))} 
              </div> 
            </motion.div> 
            <motion.div 
              initial={{opacity: 0,x: 30}} 
              animate={{opacity: 1,x: 0}} 
              transition={{duration: 0.8,delay: 0.2}} 
              className="bg-purple-50 p-8 rounded-xl" 
            > 
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Common Professions That Need Coverage</h3> 
              <div className="grid grid-cols-2 gap-3"> 
                {professions.map((profession,index)=> ( 
                  <div key={index} className="flex items-center space-x-2"> 
                    <SafeIcon icon={FiBriefcase} className="h-4 w-4 text-purple-600" /> 
                    <span className="text-gray-700 text-sm">{profession}</span> 
                  </div> 
                ))} 
              </div> 
              <div className="mt-6"> 
                <a href="https://coterie.com/quote" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block w-full bg-purple-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-purple-700 transition-colors text-center" 
                > 
                  Get My Price 
                </a> 
              </div> 
            </motion.div> 
          </div> 
        </div> 
      </section> 

      {/* Process Section */} 
      <section className="py-20 bg-purple-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8"> Seamless and Stress-Free Process </h2> 
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"> Spending time answering question after question about your business just to obtain the insurance you need is simply a waste of time. That's why we've made this process both seamless and stress-free. </p> 
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto"> 
            <h3 className="text-xl font-semibold text-gray-900 mb-4"> Coterie offers Professional Liability insurance policies that can be purchased on a monthly or annual basis. </h3> 
            <p className="text-gray-600 mb-6"> Whether you're a home-based business owner,run a micro small business,work as a contractor,own a startup,or fall into the many other types of small businesses,you have probably experienced first-hand the frustration of trying to find affordable coverage without having to jump through hoops. </p> 
            <a href="https://coterie.com/quote" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700 transition-colors" 
            > 
              Get My Price 
            </a> 
          </div> 
        </div> 
      </section> 

      {/* SEO Keywords Section */} 
      <section className="py-12 bg-gray-100"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <h3 className="text-lg font-semibold text-gray-700 mb-6 text-center"> Professional Liability Insurance Coverage Areas in California </h3> 
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm text-gray-600"> 
            {californiaKeywords.map((city,index)=> ( 
              <div key={index} className="text-center p-2 bg-white rounded-lg shadow-sm"> 
                Professional Liability Insurance {city} 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 
    </div> 
  );
};

export default ProfessionalLiability;